'use client';

import { createRoot, type Root } from 'react-dom/client';

import { LoginForm } from '@/features/forms/components/login';
import type { LoginSocialProvider } from '@/features/forms/components/login';
import {
  ConfigurableFormWidget,
  getFormConfig,
} from '@/features/forms/components/shared/configurable';
import type {
  FormButtonWidth,
  FormHeaderAlign,
  FormSubmitAlign,
  FormWidgetCountry,
  FormWidgetVariant,
  MockResult,
} from '@/features/forms/components/shared/configurable';
import { createWidgetMount } from '@/shared/utils/widget-mount';
import {
  DEFAULT_FORM_WIDGET_PROPS,
  OBSERVED_FORM_WIDGET_ATTRIBUTES,
  readFormWidgetAttribute,
  readFormWidgetProps,
} from './form-element-props';
import type { AdipaFormEventName } from './types';

function dispatchAdipaFormEvent(element: HTMLElement, name: AdipaFormEventName, detail?: unknown) {
  element.dispatchEvent(
    new CustomEvent(name, {
      detail,
      bubbles: true,
      composed: true,
    }),
  );
}

export class AdipaFormWidgetElement extends HTMLElement {
  static observedAttributes = OBSERVED_FORM_WIDGET_ATTRIBUTES;

  private root: Root | null = null;
  private mountNode: HTMLDivElement | null = null;
  private renderTimer: number | null = null;
  private unmountTimer: number | null = null;

  get variant(): FormWidgetVariant {
    return readFormWidgetAttribute(this, 'variant', DEFAULT_FORM_WIDGET_PROPS.variant);
  }

  set variant(value: FormWidgetVariant) {
    this.setAttribute('variant', value);
  }

  get platformCountry(): FormWidgetCountry {
    return (
      (this.getAttribute('platform-country') as FormWidgetCountry | null) ??
      readFormWidgetAttribute(this, 'country', DEFAULT_FORM_WIDGET_PROPS.platformCountry)
    );
  }

  set platformCountry(value: FormWidgetCountry) {
    this.setAttribute('platform-country', value);
  }

  get country(): FormWidgetCountry {
    return this.platformCountry;
  }

  set country(value: FormWidgetCountry) {
    this.platformCountry = value;
  }

  get submitUrl(): string | undefined {
    return this.getAttribute('submit-url') ?? undefined;
  }

  set submitUrl(value: string | undefined) {
    if (value) this.setAttribute('submit-url', value);
    else this.removeAttribute('submit-url');
  }

  get profileUrl(): string | undefined {
    return this.getAttribute('profile-url') ?? undefined;
  }

  set profileUrl(value: string | undefined) {
    if (value) this.setAttribute('profile-url', value);
    else this.removeAttribute('profile-url');
  }

  get termsUrl(): string | undefined {
    return this.getAttribute('terms-url') ?? undefined;
  }

  set termsUrl(value: string | undefined) {
    if (value) this.setAttribute('terms-url', value);
    else this.removeAttribute('terms-url');
  }

  get registerUrl(): string | undefined {
    return this.getAttribute('register-url') ?? undefined;
  }

  set registerUrl(value: string | undefined) {
    if (value) this.setAttribute('register-url', value);
    else this.removeAttribute('register-url');
  }

  get forgotPasswordUrl(): string | undefined {
    return this.getAttribute('forgot-password-url') ?? undefined;
  }

  set forgotPasswordUrl(value: string | undefined) {
    if (value) this.setAttribute('forgot-password-url', value);
    else this.removeAttribute('forgot-password-url');
  }

  get socialProviders(): string | undefined {
    return this.getAttribute('social-providers') ?? undefined;
  }

  set socialProviders(value: string | undefined) {
    if (value) this.setAttribute('social-providers', value);
    else this.removeAttribute('social-providers');
  }

  get googleAuthUrl(): string | undefined {
    return this.getAttribute('google-auth-url') ?? undefined;
  }

  set googleAuthUrl(value: string | undefined) {
    if (value) this.setAttribute('google-auth-url', value);
    else this.removeAttribute('google-auth-url');
  }

  get googleAuthLabel(): string | undefined {
    return this.getAttribute('google-auth-label') ?? undefined;
  }

  set googleAuthLabel(value: string | undefined) {
    if (value) this.setAttribute('google-auth-label', value);
    else this.removeAttribute('google-auth-label');
  }

  get socialDividerLabel(): string | undefined {
    return this.getAttribute('social-divider-label') ?? undefined;
  }

  set socialDividerLabel(value: string | undefined) {
    if (value) this.setAttribute('social-divider-label', value);
    else this.removeAttribute('social-divider-label');
  }

  get countdownDeadline(): string | undefined {
    return this.getAttribute('countdown-deadline') ?? undefined;
  }

  set countdownDeadline(value: string | undefined) {
    if (value) this.setAttribute('countdown-deadline', value);
    else this.removeAttribute('countdown-deadline');
  }

  get countdownText(): string | undefined {
    return this.getAttribute('countdown-text') ?? undefined;
  }

  set countdownText(value: string | undefined) {
    if (value) this.setAttribute('countdown-text', value);
    else this.removeAttribute('countdown-text');
  }

  get source(): string | undefined {
    return this.getAttribute('source') ?? undefined;
  }

  set source(value: string | undefined) {
    if (value) this.setAttribute('source', value);
    else this.removeAttribute('source');
  }

  get trackingId(): string | undefined {
    return this.getAttribute('tracking-id') ?? undefined;
  }

  set trackingId(value: string | undefined) {
    if (value) this.setAttribute('tracking-id', value);
    else this.removeAttribute('tracking-id');
  }

  get prefill(): string | undefined {
    return this.getAttribute('prefill') ?? undefined;
  }

  set prefill(value: string | undefined) {
    if (value) this.setAttribute('prefill', value);
    else this.removeAttribute('prefill');
  }

  get extraFields(): string | undefined {
    return this.getAttribute('extra-fields') ?? undefined;
  }

  set extraFields(value: string | undefined) {
    if (value) this.setAttribute('extra-fields', value);
    else this.removeAttribute('extra-fields');
  }

  get authenticated(): boolean {
    return this.getAttribute('authenticated') === 'true';
  }

  set authenticated(value: boolean) {
    if (value) this.setAttribute('authenticated', 'true');
    else this.removeAttribute('authenticated');
  }

  get titleAlign(): FormHeaderAlign | undefined {
    return (this.getAttribute('title-align') as FormHeaderAlign | null) ?? undefined;
  }

  set titleAlign(value: FormHeaderAlign | undefined) {
    if (value) this.setAttribute('title-align', value);
    else this.removeAttribute('title-align');
  }

  get formTitle(): string | undefined {
    return this.getAttribute('form-title') ?? undefined;
  }

  set formTitle(value: string | undefined) {
    if (value) this.setAttribute('form-title', value);
    else this.removeAttribute('form-title');
  }

  get buttonWidth(): FormButtonWidth | undefined {
    return (this.getAttribute('button-width') as FormButtonWidth | null) ?? undefined;
  }

  set buttonWidth(value: FormButtonWidth | undefined) {
    if (value) this.setAttribute('button-width', value);
    else this.removeAttribute('button-width');
  }

  get submitAlign(): FormSubmitAlign | undefined {
    return (this.getAttribute('submit-align') as FormSubmitAlign | null) ?? undefined;
  }

  set submitAlign(value: FormSubmitAlign | undefined) {
    if (value) this.setAttribute('submit-align', value);
    else this.removeAttribute('submit-align');
  }

  get recaptchaSiteKey(): string | undefined {
    return this.getAttribute('recaptcha-site-key') ?? undefined;
  }

  set recaptchaSiteKey(value: string | undefined) {
    if (value) this.setAttribute('recaptcha-site-key', value);
    else this.removeAttribute('recaptcha-site-key');
  }

  get successRedirectUrl(): string | undefined {
    return this.getAttribute('success-redirect-url') ?? undefined;
  }

  set successRedirectUrl(value: string | undefined) {
    if (value) this.setAttribute('success-redirect-url', value);
    else this.removeAttribute('success-redirect-url');
  }

  get naked(): boolean {
    return this.hasAttribute('naked');
  }

  set naked(value: boolean) {
    if (value) this.setAttribute('naked', '');
    else this.removeAttribute('naked');
  }

  get mockResult(): MockResult {
    return readFormWidgetAttribute(this, 'mock-result', DEFAULT_FORM_WIDGET_PROPS.mockResult);
  }

  set mockResult(value: MockResult) {
    this.setAttribute('mock-result', value);
  }

  connectedCallback() {
    if (this.unmountTimer !== null) {
      window.clearTimeout(this.unmountTimer);
      this.unmountTimer = null;
    }

    if (!this.mountNode) {
      this.mountNode = createWidgetMount(this) as HTMLDivElement;
    }

    if (!this.root) {
      this.root = createRoot(this.mountNode);
    }

    this.scheduleRender();
  }

  disconnectedCallback() {
    if (!this.root || this.unmountTimer !== null) return;

    if (this.renderTimer !== null) {
      window.clearTimeout(this.renderTimer);
      this.renderTimer = null;
    }

    const root = this.root;
    this.unmountTimer = window.setTimeout(() => {
      this.unmountTimer = null;

      if (this.isConnected || this.root !== root) return;

      root.unmount();
      this.root = null;
    }, 0);
  }

  attributeChangedCallback() {
    this.scheduleRender();
  }

  private scheduleRender() {
    if (!this.root || this.renderTimer !== null) return;

    this.renderTimer = window.setTimeout(() => {
      this.renderTimer = null;
      this.renderWidget();
    }, 0);
  }

  private renderWidget() {
    if (!this.root) return;

    const props = readFormWidgetProps(this);

    if (props.variant === 'login') {
      dispatchAdipaFormEvent(this, 'adipa-form-ready', {
        formId: 'login',
        variant: props.variant,
        platformCountry: props.platformCountry,
        context: {
          source: 'login-widget',
        },
      });

      this.root.render(
        <LoginForm
          key={`${props.variant}-${props.platformCountry}-${props.submitUrl ?? ''}`}
          platformCountry={props.platformCountry}
          submitUrl={props.submitUrl}
          registerUrl={props.registerUrl}
          forgotPasswordUrl={props.forgotPasswordUrl}
          socialProviders={createLoginSocialProviders(props)}
          socialDividerLabel={props.socialDividerLabel}
          source={props.source}
          trackingId={props.trackingId}
          pageUrl={props.pageUrl}
          scenario={props.previewScenario}
          titleAlign={props.titleAlign}
          buttonWidth={props.buttonWidth}
          mockResult={props.mockResult}
          requireSubmitUrl
          onSubmitPayload={(payload) => dispatchAdipaFormEvent(this, 'adipa-form-submit', payload)}
          onSuccess={(payload) => dispatchAdipaFormEvent(this, 'adipa-form-success', payload)}
          onValidationError={(error) =>
            dispatchAdipaFormEvent(this, 'adipa-form-validation-error', {
              formId: 'login',
              variant: props.variant,
              platformCountry: props.platformCountry,
              error,
            })
          }
          onError={(error) =>
            dispatchAdipaFormEvent(this, 'adipa-form-error', {
              formId: 'login',
              variant: props.variant,
              platformCountry: props.platformCountry,
              error,
            })
          }
          onSocialAuth={(provider) =>
            dispatchAdipaFormEvent(this, 'adipa-form-social-auth', {
              formId: 'login',
              variant: props.variant,
              platformCountry: props.platformCountry,
              provider,
            })
          }
        />,
      );
      return;
    }

    const config = getFormConfig(props.variant, props.platformCountry);

    if (!config) {
      this.root.render(null);
      dispatchAdipaFormEvent(this, 'adipa-form-error', {
        variant: props.variant,
        platformCountry: props.platformCountry,
        error: 'Unsupported ADIPA form widget configuration.',
      });
      return;
    }

    dispatchAdipaFormEvent(this, 'adipa-form-ready', {
      formId: config.formId,
      variant: config.variant,
      platformCountry: config.country,
      context: config.context ?? {},
    });

    this.root.render(
      <ConfigurableFormWidget
        key={`${props.variant}-${props.platformCountry}-${props.submitUrl ?? ''}`}
        config={config}
        submitUrl={props.submitUrl}
        profileUrl={props.profileUrl}
        termsUrl={props.termsUrl}
        countdownDeadline={props.countdownDeadline}
        countdownText={props.countdownText}
        source={props.source}
        trackingId={props.trackingId}
        pageUrl={props.pageUrl}
        prefillValues={props.prefill}
        extraFields={props.extraFields}
        scenario={props.previewScenario}
        titleAlign={props.titleAlign}
        formTitle={props.formTitle}
        submitAlign={props.submitAlign}
        buttonWidth={props.buttonWidth}
        recaptchaSiteKey={props.recaptchaSiteKey}
        successRedirectUrl={props.successRedirectUrl}
        naked={props.naked}
        mockResult={props.mockResult}
        requireSubmitUrl
        onSubmitPayload={(payload) => dispatchAdipaFormEvent(this, 'adipa-form-submit', payload)}
        onSuccess={(payload) => dispatchAdipaFormEvent(this, 'adipa-form-success', payload)}
        onValidationError={(error) =>
          dispatchAdipaFormEvent(this, 'adipa-form-validation-error', {
            formId: config.formId,
            variant: config.variant,
            platformCountry: config.country,
            error,
          })
        }
        onError={(error) =>
          dispatchAdipaFormEvent(this, 'adipa-form-error', {
            formId: config.formId,
            variant: config.variant,
            platformCountry: config.country,
            error,
          })
        }
      />,
    );
  }
}

function createLoginSocialProviders(
  props: ReturnType<typeof readFormWidgetProps>,
): LoginSocialProvider[] {
  const enabledProviders = (props.socialProviders ?? '')
    .split(',')
    .map((provider) => provider.trim().toLowerCase())
    .filter(Boolean);

  return enabledProviders.flatMap((providerId): LoginSocialProvider[] => {
    if (providerId === 'google') {
      return [
        {
          id: 'google',
          label: props.googleAuthLabel ?? 'Google',
          authUrl: props.googleAuthUrl,
        },
      ];
    }

    return [];
  });
}
